/**
 * Vencimiento comercial de facturas de venta.
 * El plazo/fecha del comprobante registrado es independiente del maestro de cliente actual.
 */

import { toDateOnly } from './facturaVentaFechas';
import {
  calcularFechaVencimDesdeVtoComprob,
  inferirVtoComprobDesdeFechas,
} from './vtoComprobCliente';

export type VencimComprobanteCargado = {
  fechaVencim: Date;
  /** Plazo comercial del comprobante (API o inferido desde fechas guardadas). */
  vtoComprob: number | null;
  /** true si el API devolvió fechaVencim explícita. */
  desdeApi: boolean;
};

function readNested(raw: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    const value = raw[key];
    if (value != null && value !== '') return value;
  }
  return undefined;
}

/** Lee fechaVencim del response del API (variantes camelCase/PascalCase y formulario anidado). */
export function resolveFechaVencimFromApi(raw: unknown): string | undefined {
  if (raw == null || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const formulario = o.formulario ?? o.Formulario;
  const candidates: unknown[] = [
    o.fechaVencim,
    o.FechaVencim,
    o.fechaVencimiento,
    o.FechaVencimiento,
  ];
  if (formulario != null && typeof formulario === 'object') {
    const f = formulario as Record<string, unknown>;
    candidates.push(f.fechaVencim, f.FechaVencim, f.fechaVencimiento, f.FechaVencimiento);
  }
  for (const value of candidates) {
    const d = toDateOnly(value as string | Date);
    if (d) return d.toISOString();
  }
  return undefined;
}

/** Plazo comercial persistido en el comprobante (cuando el backend lo exponga). */
export function resolveVtoComprobComprobanteFromApi(raw: unknown): number | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const formulario = o.formulario ?? o.Formulario;
  const rawVto = readNested(o, [
    'vtoComprob',
    'VtoComprob',
    'vtoComprobComprobante',
    'VtoComprobComprobante',
    'vto_comprob',
  ]);
  if (rawVto != null) {
    const n = Number(rawVto);
    return Number.isFinite(n) ? n : null;
  }
  if (formulario != null && typeof formulario === 'object') {
    const f = formulario as Record<string, unknown>;
    const nested = readNested(f, ['vtoComprob', 'VtoComprob', 'vto_comprob']);
    if (nested != null) {
      const n = Number(nested);
      return Number.isFinite(n) ? n : null;
    }
  }
  return null;
}

export function esComprobanteRegistrado(nroFormulario?: number | null): boolean {
  return Number(nroFormulario) > 0;
}

/**
 * Mapea vencimiento al abrir un comprobante ya emitido.
 * Prioridad: fechaVencim del API → inferencia desde fechas → emisión (sin recalcular con cliente).
 */
export function mapearVencimComprobanteCargado(
  raw: unknown,
  fechaEmision: string | Date,
): VencimComprobanteCargado {
  const emision = toDateOnly(fechaEmision) ?? new Date();
  const fechaVencimApi = resolveFechaVencimFromApi(raw);
  const vtoComprobApi = resolveVtoComprobComprobanteFromApi(raw);

  if (fechaVencimApi) {
    const fechaVencim = new Date(fechaVencimApi);
    const vtoComprob =
      vtoComprobApi ?? inferirVtoComprobDesdeFechas(emision, fechaVencim);
    return { fechaVencim, vtoComprob, desdeApi: true };
  }

  if (vtoComprobApi != null) {
    const fechaVencim = calcularFechaVencimDesdeVtoComprob(emision, vtoComprobApi);
    return { fechaVencim, vtoComprob: vtoComprobApi, desdeApi: false };
  }

  return { fechaVencim: emision, vtoComprob: null, desdeApi: false };
}
